<style lang="scss">
.file-list {
    display: flex;
    margin: 16px 0;

    :global(.file-list-item) {
        margin: 0 16px 16px 0;
    }
}

.pick-files {
    overflow: hidden;
    position: relative;
    width: 100px;
    height: 100px;

    label {
        display: block;
        width: 100%;
        height: 100%;
        box-sizing: border-box;
        display: flex;
        text-align: center;
        align-items: center;
        border-radius: 5px;
        cursor: pointer;
        background: #f9d9eb;
        color: #333333;
    }

    input {
        position: absolute;
        width: 0;
        height: 0;
        left: 100%;
        visibility: hidden;
    }
}
</style>
<script lang="ts">
import { createEventDispatcher } from 'svelte';
import type { UploadResponse } from '../api/post';
import { Command } from '../api/types';
import type { FileData, ResponseError, FileDto } from '../api/types';
import FileListItem from './FileListItem.svelte';

interface PendingFile {
    file: File;
    xhr?: XMLHttpRequest;
    localUrl: string;
    progress: number;
    token?: string;
    error?: Error;
}

export let files: FileData[];
let deleted = new Set<string>();
let added: PendingFile[] = [];
let prev: FileData[];
let fileInput: HTMLInputElement;
const dispatch = createEventDispatcher();

$: {
    if (prev !== files) {
        added.forEach(file => URL.revokeObjectURL(file.localUrl));
        deleted = new Set();
        added = [];
        prev = files;
    }
}

function deleteFile(file: FileData | PendingFile) {
    if ('file' in file) {
        // Удаляем загружаемый файл
        if (file.xhr) {
            file.xhr.abort();
        }
        added = added.filter(f => f !== file);
    } else {
        // Удаляем уже существующий файл, в данном случае действует как переключатель
        deleted = new Set(deleted);
        if (deleted.has(file.id)) {
            deleted.delete(file.id);
        } else {
            deleted.add(file.id);
        }
    }

    dispatch('delete', getDelta());
}

/**
 * Добавление указанного файла
 */
function addFile(file: File) {
    const localUrl = URL.createObjectURL(file);
    const xhr = new XMLHttpRequest();
    const item: PendingFile = {
        file,
        xhr,
        localUrl,
        progress: 0,
    };

    notifyUploadStart();

    added = [...added, item];

    xhr.open('POST', Command.Upload, true);
    // xhr.setRequestHeader('Accept', 'application/json');
    xhr.upload.onprogress = evt => updatePending(file, { progress: evt.loaded / evt.total });

    xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
            const resp: UploadResponse = JSON.parse(xhr.responseText || '{}');
            if (resp?.success) {
                updatePending(file, {
                    xhr: null,
                    token: resp.uploads[0].uploadToken
                });
            } else {
                updatePending(file, {
                    xhr: null,
                    error: new Error('Не удалось получить правильный ответ от сервера')
                });
            }
        } else {
            let error: Error;
            const resp: ResponseError = JSON.parse(xhr.responseText || '{}');
            if (resp && resp.success === false && resp.message) {
                error = new Error(resp.message);
            } else {
                error = new Error(`${xhr.statusText} (${xhr.status})`);
            }

            updatePending(file, { xhr: null, error });
        }

        notifyUploadEnd();
    };
    xhr.onerror = () => {
        updatePending(file, {
            xhr: null,
            error: new Error('Не удалось загрузить файл')
        });
        notifyUploadEnd();
    };

    const formData = new FormData();
    formData.set('files', file);
    xhr.send(formData);
}

function updatePending(file: File, data: Partial<PendingFile>) {
    const ix = added.findIndex(f => f.file === file);
    if (ix !== -1) {
        added = [...added];
        added[ix] = { ...added[ix], ...data };
    }
}

function onPickFiles() {
    if (fileInput.files) {
        for (const f of fileInput.files) {
            addFile(f);
        }
    }
}

function notifyUploadStart() {
    if (added.every(item => !item.xhr)) {
        dispatch('upload-start');
    }
}

function notifyUploadEnd() {
    if (added.every(item => !item.xhr)) {
        dispatch('upload-end', getDelta());
    }
}

function getDelta(): FileDto[] {
    const result: FileDto[] = [];
    for (const id of deleted) {
        result.push({ id, delete: true });
    }

    for (const item of added) {
        if (item.token) {
            result.push({ uploadToken: item.token, abbr: 'image' });
        }
    }

    return result;
}
</script>

<div class="file-list">
    {#each files as file (file.id)}
        <FileListItem src={file.url} deleted={deleted.has(file.id)} on:delete={() => deleteFile(file)} />
    {/each}
    {#each added as file}
        <FileListItem src={file.localUrl} progress={file.xhr ? file.progress : null} on:delete={() => deleteFile(file)} />
    {/each}
    <div class="pick-files">
        <label>
            + Добавить картинку
            <input type="file" multiple accept="image/png, image/jpeg"
                on:change={onPickFiles}
                bind:this={fileInput} />
        </label>
    </div>
</div>
